'use client';

import { useCallback, useState, useEffect } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { useHostControl } from './HostControlContext';
import { RoomEvent } from 'livekit-client';
import toast from 'react-hot-toast';

// Constante para o tópico de controle
export const CONTROL_EVENTS_TOPIC = 'CONTROL_EVENTS';

// Hook para verificar o estado atual do spotlight global
export function useGlobalSpotlightState() {
  const room = useRoomContext();
  const [globalSpotlightTarget, setGlobalSpotlightTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!room) return;

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: { identity?: string },
      kind?: any,
      topic?: string
    ) => {
      if (topic !== CONTROL_EVENTS_TOPIC) return;

      try {
        const message = JSON.parse(new TextDecoder().decode(payload));

        if (message.action === 'GLOBAL_SPOTLIGHT' && message.targetIdentity) {
          setGlobalSpotlightTarget(message.targetIdentity);
        } else if (message.action === 'REMOVE_SPOTLIGHT') {
          setGlobalSpotlightTarget(null);
        }
      } catch (error) {
        // Ignora erros de parsing
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room]);

  return { globalSpotlightTarget };
}

// Função para enviar mensagem de Spotlight Global
export function useGlobalSpotlight() {
  const room = useRoomContext();
  const { isHost, hasModeratorPermissions } = useHostControl();

  const toggleGlobalSpotlight = useCallback(
    async (targetIdentity: string | null) => {
      if (!room?.localParticipant) {
        console.warn('Room ou localParticipant não disponível');
        return;
      }

      // Verifica permissões
      if (!isHost && !hasModeratorPermissions) {
        toast.error('Apenas anfitriões podem destacar vídeos para todos');
        return;
      }

      try {
        const payload = JSON.stringify({
          action: targetIdentity ? 'GLOBAL_SPOTLIGHT' : 'REMOVE_SPOTLIGHT',
          targetIdentity: targetIdentity,
          timestamp: Date.now(),
        });

        await room.localParticipant.publishData(
          new TextEncoder().encode(payload),
          {
            reliable: true,
            topic: CONTROL_EVENTS_TOPIC,
          }
        );

        if (targetIdentity) {
          toast.success('Vídeo destacado para todos os participantes');
        } else {
          toast.success('Destaque removido');
        }
      } catch (error) {
        console.error('Erro ao enviar mensagem de spotlight global:', error);
        toast.error('Erro ao destacar vídeo');
      }
    },
    [room, isHost, hasModeratorPermissions]
  );

  return { toggleGlobalSpotlight, canControlSpotlight: isHost || hasModeratorPermissions };
}

